import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  SettingsBackupRestore as ResetIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../design-system/providers/ThemeProvider';
import AdminNavigation from '../../organisms/AdminNavigation';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const { user } = useAuth();
  const { currentTheme, setTheme, resetThemeSettings } = useTheme();

  // Ir para interface do cliente (curadoria)
  const handleGoToClientInterface = () => {
    navigate('/');
  };

  const isDarkMode = currentTheme.id === 'escuro';
  const toggleDarkMode = () => {
    const newTheme = isDarkMode ? 'light' : 'escuro';
    setTheme(newTheme);
  };

  const drawerWidth = 240;
  const miniDrawerWidth = 60;

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleResetDialogOpen = () => {
    setResetDialogOpen(true);
  };

  const handleResetDialogClose = () => {
    setResetDialogOpen(false);
  };

  const handleConfirmReset = () => {
    resetThemeSettings();
    setResetDialogOpen(false);
  };

  return (
    <Box sx={{ 
      display: 'flex',
      backgroundColor: 'background.default', // Fundo do tema para toda a tela
      minHeight: '100vh' // Altura mínima da tela
    }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1300, // Z-index maior que o sidebar
          width: '100%', // Ocupa toda a largura
          ml: 0, // Sem margem
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Mensure Monitor - Painel Administrativo
          </Typography>

          <Box display="flex" alignItems="center" gap={2}>
            {/* Botao para ir para interface do cliente */}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<DashboardIcon />}
              onClick={handleGoToClientInterface}
              sx={{
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.8)',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Interface Cliente
            </Button>
            <IconButton
              color="inherit"
              onClick={handleResetDialogOpen}
              title="Resetar configurações de tema"
            >
              <ResetIcon />
            </IconButton>
            <IconButton color="inherit" onClick={toggleDarkMode} title="Alternar tema">
              {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            <Typography variant="body2">
              {user?.name || 'Usuário'}
            </Typography>
            <IconButton color="inherit">
              <AccountCircleIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      
      <AdminNavigation
        open={drawerOpen}
        drawerWidth={drawerWidth}
        miniDrawerWidth={miniDrawerWidth}
      />
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0, // Sem padding
          '--admin-sidebar-width': `${drawerOpen ? drawerWidth : miniDrawerWidth}px`,
          '--admin-appbar-height': '64px',
          width: `calc(100% - ${drawerOpen ? drawerWidth : miniDrawerWidth}px)`,
          ml: `${drawerOpen ? drawerWidth : miniDrawerWidth}px`,
          mt: 8, // Margem top para compensar o AppBar
          transition: 'width 0.3s ease, margin-left 0.3s ease',
          backgroundColor: 'background.default', // Fundo do tema
          minHeight: 'calc(100vh - 64px)', // Altura mínima para cobrir toda a tela
        }}
      >
        {children}
      </Box>

      {/* Diálogo de confirmação para resetar configurações */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleResetDialogClose}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">
          Resetar Configurações de Tema?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            Esta ação irá resetar todas as suas preferências de tema (tema, fonte, tamanho e cor primária) para os valores padrão.
            Todas as customizações serão perdidas.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetDialogClose} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirmReset} color="error" variant="contained" autoFocus>
            Resetar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLayout;
